import { useEffect, useState } from 'react';
import { Undo2 } from 'lucide-react';
import { Avatar } from '@/components/apps/Avatar';
import { type AppCollection, type AppMember, type AppRecord, type AppRecordEvent, loadRecordEvents, UNDOABLE_EVENTS } from '@/lib/app-records';
import { statusOf } from './app-records-model';

interface AppRecordHistoryProps {
  record: AppRecord;
  collection: AppCollection;
  members: AppMember[];
  /** Changes that do not touch the record itself (label, comment) bump it. */
  refreshKey?: number;
  onUndo: (eventId: string) => Promise<void>;
}

const formatDate = (value: unknown) => typeof value === 'string' && value
  ? new Date(`${value}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : 'aucune';

/** Who did what and when; a person's change can be undone, which adds a line. */
export const AppRecordHistory = ({ record, collection, members, refreshKey = 0, onUndo }: AppRecordHistoryProps) => {
  const [events, setEvents] = useState<AppRecordEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const nameOf = (userId: unknown) => members.find((member) => member.userId === userId)?.name ?? 'Quelqu’un';

  useEffect(() => {
    let active = true;
    loadRecordEvents(record.id)
      .then((list) => { if (active) { setEvents(list); setError(null); } })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : 'Historique indisponible.'); });
    return () => { active = false; };
  }, [record.id, record.version, record.lastRunId, refreshKey]);

  const undone = new Set(events.map((event) => event.undoesEventId).filter(Boolean));
  const describe = (event: AppRecordEvent): string => {
    switch (event.kind) {
      case 'created': return 'Ajoutée par une exécution';
      case 'updated_by_run': return 'Mise à jour par une exécution';
      case 'status_changed': return `Statut : ${statusOf(collection, String(event.before)).label} → ${statusOf(collection, String(event.after)).label}`;
      case 'assigned': return event.after ? `Assignée à ${nameOf(event.after)}` : 'Assignation retirée';
      case 'due_date_changed': return `Échéance : ${formatDate(event.after)}`;
      case 'archived': return 'Archivée';
      case 'restored': return 'Sortie des archives';
      case 'undone': return 'Changement annulé';
      case 'labeled': return `Étiquette « ${String(event.after)} » posée`;
      case 'unlabeled': return `Étiquette « ${String(event.after)} » retirée`;
      case 'commented': return `A commenté : « ${String(event.after)} »`;
      case 'reviewed': return 'Revue enregistrée';
      case 'attached': return `« ${String((event.after as { title?: unknown } | null)?.title ?? 'Une ligne')} » rattachée ici`;
      case 'cell_edited': {
        const key = (event.field ?? '').replace(/^cell:/, '');
        const label = collection.columns.find((column) => column.key === key)?.label ?? key;
        const after = event.after as { value?: unknown; source?: string } | null;
        return after ? `${label} corrigé${after.source === 'chat' ? ' depuis le chat' : ''} : « ${String(after.value)} »` : `${label} : valeur de Domino rétablie`;
      }
    }
  };

  if (error) return <p className="text-sm text-domino-inkSoft">{error}</p>;
  return (
    <ol className="space-y-2">
      {events.map((event) => (
        <li key={event.id} className="flex items-start gap-2 text-sm">
          {event.actorUserId ? <Avatar name={nameOf(event.actorUserId)} size="xs" /> : <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-domino-cardBorder" aria-hidden="true" />}
          <div className="min-w-0 flex-1">
            <p className="text-domino-ink">{describe(event)}</p>
            <p className="text-xs text-domino-inkSoft">
              {event.actorUserId ? `${nameOf(event.actorUserId)} · ` : ''}
              {new Date(event.createdAt).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          {UNDOABLE_EVENTS.has(event.kind) && !undone.has(event.id) && (
            <button type="button" onClick={() => void onUndo(event.id)} aria-label={`Annuler : ${describe(event)}`}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-domino-aubergine hover:bg-domino-muted">
              <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />Annuler
            </button>
          )}
        </li>
      ))}
    </ol>
  );
};
